/* RESPONSIBLE TEAM: team-frontend-tech */

import type TracingService from 'embercom/services/tracing';
import type ReactTeammateAppIslandService from 'embercom/services/react-teammate-app-island';

/**
 * Subscribes to React tracing bridge events and routes them to `TracingService`.
 * Called once from `onAppReady()` when the React island boots.
 *
 * React dispatches tracing events via the bridge; this function subscribes to
 * them, looks up the matching root span by `resource`, and calls the matching
 * `TracingService` method. Every attribute bag is stamped with a `source: 'react'`
 * tag so spans from the React app are trivially filterable in Honeycomb.
 */
export function setupReactTracingBridge(
  tracing: TracingService,
  island: ReactTeammateAppIslandService,
): () => void {
  let unsubscribers: Array<() => void> = [];

  unsubscribers.push(
    island.subscribeToTeammateAppEvent('tracing:start-span', (data: Record<string, unknown>) => {
      let resource = data['resource'] as string;
      let attributes = (data['attributes'] as Record<string, unknown>) ?? {};
      let keepAlive = data['keepAlive'] === true;
      tracing.startRootSpan({
        name: resource,
        resource,
        attributes: { ...attributes, started_by: 'react' },
        keepAlive,
      });
    }),
  );

  unsubscribers.push(
    island.subscribeToTeammateAppEvent('tracing:end-span', (data: Record<string, unknown>) => {
      let resource = data['resource'] as string;
      let attributes = (data['attributes'] as Record<string, unknown>) ?? {};
      let span = tracing.getActiveRootSpanByResource(resource);
      // Guard against double-end: `TracingService.endChildSpan` will happily
      // call `span.end()` twice, which corrupts the trace. Skip if already ended.
      if (span && !span.ended) {
        tracing.endChildSpan({ span, attributes: { ...attributes, ended_by: 'react' } });
      }
    }),
  );

  unsubscribers.push(
    island.subscribeToTeammateAppEvent('tracing:tag-span', (data: Record<string, unknown>) => {
      let resource = data['resource'] as string | undefined;
      let tags = (data['tags'] as Record<string, unknown>) ?? {};
      if (resource) {
        let span = tracing.getActiveRootSpanByResource(resource);
        span?.setAttributes({ ...tags, tagged_by: 'react' });
      } else {
        tracing.tagActiveSpan({ ...tags, tagged_by: 'react' });
      }
    }),
  );

  unsubscribers.push(
    island.subscribeToTeammateAppEvent('tracing:add-event', (data: Record<string, unknown>) => {
      let name = data['name'] as string;
      let tags = (data['tags'] as Record<string, unknown>) ?? {};
      tracing.addEvent(name, { ...tags, event_from: 'react' });
    }),
  );

  unsubscribers.push(
    island.subscribeToTeammateAppEvent('tracing:tag-root-span', (data: Record<string, unknown>) => {
      let tags = (data['tags'] as Record<string, unknown>) ?? {};
      tracing.tagRootSpan({ ...tags, tagged_by: 'react' });
    }),
  );

  unsubscribers.push(
    island.subscribeToTeammateAppEvent('tracing:halt-trace', (data: Record<string, unknown>) => {
      let reason = data['reason'] as string | undefined;
      tracing.haltTrace(reason);
    }),
  );

  return () => {
    for (let unsubscribe of unsubscribers) {
      unsubscribe();
    }
  };
}
